import React from 'react';
import { Link } from 'react-router-dom';
import RevealOnScroll from './RevealOnScroll';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
    const { t } = useLanguage();

    return (
        <footer className="bg-white border-t border-gray-100 mt-12">
            <div className="container mx-auto py-12 px-4 sm:px-6 lg:px-8 text-gray-600">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center md:text-start">
                    {/* Column 1: Quick Links */}
                    <RevealOnScroll className="flex flex-col items-center md:items-start">
                        <h3 className="text-sm font-serif font-semibold uppercase tracking-widest text-gray-800 mb-4">
                            {t('quickLinks')}
                        </h3>
                        <ul className="space-y-2.5 text-sm">
                            <li>
                                <Link to="/return-policy?tab=return" className="hover:text-[#8B6939] transition-colors duration-200">
                                    {t('returnAndExchangePolicy')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/return-policy?tab=buying" className="hover:text-[#8B6939] transition-colors duration-200">
                                    {t('buyingAndShippingPolicy')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/return-policy?tab=terms" className="hover:text-[#8B6939] transition-colors duration-200">
                                    {t('termsAndConditions')}
                                </Link>
                            </li>
                        </ul>
                    </RevealOnScroll>

                    {/* Column 2: Find Us & Contact Info */}
                    <RevealOnScroll delay={80} className="flex flex-col items-center">
                        <h3 className="text-sm font-serif font-semibold uppercase tracking-widest text-gray-800 mb-4">
                            {t('findUsHere')}
                        </h3>
                        <div className="flex justify-center items-center gap-4 mb-4">
                            <a
                                href="https://www.facebook.com/share/19k89ojnky/?mibextid=wwXIfr"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
                            >
                                <img
                                    loading="lazy"
                                    decoding="async"
                                    src="https://firebasestorage.googleapis.com/v0/b/ukiyo-store.firebasestorage.app/o/Facebook_Logo_Picture_Panel_1200x1200.webp?alt=media&token=bac3e965-c54a-4803-a251-59e34d85c6a6"
                                    alt="Facebook"
                                    className="w-8 h-8 object-contain"
                                />
                            </a>
                            <a
                                href="https://www.instagram.com/ukiyo_homes?igsh=dHhkZzkxZGxrajNk"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
                            >
                                <img
                                    loading="lazy"
                                    decoding="async"
                                    src="https://firebasestorage.googleapis.com/v0/b/ukiyo-store.firebasestorage.app/o/instagram%20logo.png?alt=media&token=466a7697-4a1e-4e06-840b-7b605eb038a8"
                                    alt="Instagram"
                                    className="w-8 h-8 object-contain p-1"
                                />
                            </a>
                        </div>

                        <div className="text-sm">
                            <a
                                href="https://maps.app.goo.gl/czR9kk8zGL2aYA1Q8"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-[#8B6939] transition-colors duration-200 flex items-center justify-center gap-1 text-gray-600"
                            >
                                <span>📍</span> {t('storeAddress')}
                            </a>
                        </div>

                        <p className="text-sm mt-2">
                            <a
                                href="https://wa.me/201116663092"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:underline text-gray-600 hover:text-[#8B6939] transition-colors duration-200"
                            >
                                {t('whatsappLabel')}: <span className="font-medium text-[#8B6939]">01116663092</span>
                            </a>
                        </p>

                        <p className="text-sm mt-1.5">
                            <a href="mailto:support@ukiyo-homes.com" className="hover:underline text-[#8B6939]">
                                support@ukiyo-homes.com
                            </a>
                        </p>
                    </RevealOnScroll>

                    {/* Column 3: We Accept (Visa, Mastercard, Amex & Meeza) */}
                    <RevealOnScroll delay={160} className="flex flex-col items-center md:items-end">
                        <h3 className="text-sm font-serif font-semibold uppercase tracking-widest text-gray-800 mb-4">
                            {t('weAccept')}
                        </h3>
                        <div className="flex flex-wrap items-center justify-center md:justify-end gap-2.5">
                            {/* Visa Logo Badge */}
                            <div
                                className="h-9 px-3 bg-white border border-gray-200 rounded-md shadow-sm flex items-center justify-center"
                                title="Visa"
                            >
                                <svg className="h-4 w-auto" viewBox="0 0 780 500" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M293.2 348.73L326.56 152.92H379.91L346.54 348.73H293.2Z" fill="#1A1F71" />
                                    <path d="M540.1 157.7C529.55 153.75 513 149.5 492.32 149.5C439.67 149.5 402.58 176.02 402.27 214.03C401.96 242.14 428.73 257.82 448.92 267.18C469.67 276.82 476.64 282.99 476.55 291.61C476.42 304.8 459.93 310.82 444.57 310.82C423.16 310.82 411.79 307.84 394.22 300.51L387.32 297.37L379.82 341.48C392.32 346.96 415.42 351.72 439.4 351.97C495.59 351.97 532.06 325.66 532.49 285.07C532.7 262.78 518.49 245.81 487.64 231.83C468.97 222.77 457.54 216.73 457.66 207.52C457.66 199.37 467.35 190.66 488.27 190.66C505.75 190.38 518.43 194.2 528.35 198.18L533.17 200.44L540.1 157.7Z" fill="#1A1F71" />
                                    <path d="M678.5 152.92H637.16C624.36 152.92 614.79 156.41 609.18 169.18L529.8 348.73H585.94C585.94 348.73 595.12 324.6 597.2 319.31C603.34 319.31 657.85 319.39 665.67 319.39C667.27 326.25 672.18 348.73 672.18 348.73H721.81L678.5 152.92ZM612.82 279.31C617.24 267.97 634.12 224.21 634.12 224.21C633.81 224.75 638.51 212.78 641.22 205.35L644.84 222.39C644.84 222.39 655.06 269.62 657.22 279.31H612.82Z" fill="#1A1F71" />
                                    <path d="M247.92 152.92L195.55 286.68L189.96 259.54C180.24 228.18 149.91 194.15 116.05 177.21L163.93 348.55L220.47 348.48L304.53 152.92H247.92Z" fill="#1A1F71" />
                                    <path d="M146.92 152.92H60.62L59.93 157.02C127.09 173.32 171.54 212.75 189.96 259.54L171.2 169.47C167.96 156.81 158.59 153.36 146.92 152.92Z" fill="#F9A533" />
                                </svg>
                            </div>

                            {/* Mastercard Logo Badge */}
                            <div
                                className="h-9 px-3 bg-white border border-gray-200 rounded-md shadow-sm flex items-center justify-center"
                                title="Mastercard"
                            >
                                <svg className="h-5 w-auto" viewBox="0 0 152 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="52" cy="50" r="40" fill="#EB001B" />
                                    <circle cx="100" cy="50" r="40" fill="#F79E1B" />
                                    <path
                                        d="M76 18.78C86.3 26.1 93 37.35 93 50C93 62.65 86.3 73.9 76 81.22C65.7 73.9 59 62.65 59 50C59 37.35 65.7 26.1 76 18.78Z"
                                        fill="#FF5F00"
                                    />
                                </svg>
                            </div>

                            {/* American Express (Amex) Logo Badge */}
                            <div
                                className="h-9 px-1 bg-white border border-gray-200 rounded-md shadow-sm flex items-center justify-center overflow-hidden"
                                title="American Express (Amex)"
                            >
                                <img
                                    src="/amex-logo.png"
                                    alt="AMEX"
                                    className="h-7 w-auto object-contain rounded-[4px]"
                                />
                            </div>

                            {/* Meeza Logo Badge */}
                            <div
                                className="h-9 px-2 bg-white border border-gray-200 rounded-md shadow-sm flex items-center justify-center"
                                title="Meeza"
                            >
                                <img
                                    src="/meeza-logo.png"
                                    alt="Meeza"
                                    className="h-6 w-auto object-contain"
                                />
                            </div>
                        </div>
                    </RevealOnScroll>
                </div>

                {/* Bottom Copyright Bar */}
                <div className="mt-10 pt-6 border-t border-gray-100 text-center">
                    <p className="text-xs text-gray-400">
                        &copy; {new Date().getFullYear()} {t('allRightsReserved')}
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default React.memo(Footer);
